/**
 * SSR Data Parsers
 *
 * Utilities for parsing and validating SSR data passed from Django to React templates.
 * These parsers use io-ts codecs to ensure type safety at runtime.
 */
import * as t from "io-ts";

import {
    IModularConfiguratorPromoCard,
    IModularConfiguratorUpsellInfoModal,
    ModularConfiguratorPromoCards,
    ModularConfiguratorUpsellInfoModals,
} from "../apps/configurator/models";
import {
    ConcreteBundles,
    IConcreteBundle,
    IProduct,
    IVerboseProductClass,
    Products,
    VerboseProductClass,
} from "../models/catalogue";
import { FinancingPlan, FinancingPlans } from "../models/financing";
import { IReviewsProductID, ReviewsProductID } from "../models/nominals";
import {
    IReviewResultsPage,
    IReviewsProduct,
    ISearchFacet,
    IWriteReviewTemplate,
    ReviewResultsPage,
    ReviewsProducts,
    SearchFacets,
    WriteReviewTemplate,
} from "../models/reviews";
import { check } from "../models/utils";

/**
 * Parse and validate SSR product data using io-ts codecs.
 */
export const parseSSRProducts = (
    productsDataJson: string,
): readonly IProduct[] => {
    const trimmed = productsDataJson?.trim();
    if (!trimmed || trimmed === "null" || trimmed === "[]") {
        return [];
    }
    let rawData: unknown;
    try {
        rawData = JSON.parse(trimmed);
    } catch (e) {
        console.error("Failed to parse SSR products data:", e);
        return [];
    }
    return check(Products.decode(rawData));
};

/**
 * Parse and validate SSR product class data using io-ts codecs.
 */
export const parseSSRProductClass = (
    productClassDataJson: string,
): IVerboseProductClass | null => {
    const trimmed = productClassDataJson?.trim();
    if (!trimmed || trimmed === "null") {
        return null;
    }
    let rawData: unknown;
    try {
        rawData = JSON.parse(trimmed);
    } catch (e) {
        console.error("Failed to parse SSR product class data:", e);
        return null;
    }
    return check(VerboseProductClass.decode(rawData));
};

/**
 * Parse and validate SSR financing plans data using io-ts codecs.
 */
export const parseSSRFinancingPlans = (
    financingPlansDataJson: string,
): readonly FinancingPlan[] => {
    const trimmed = financingPlansDataJson?.trim();
    if (!trimmed || trimmed === "null" || trimmed === "[]") {
        return [];
    }
    let rawData: unknown;
    try {
        rawData = JSON.parse(trimmed);
    } catch (e) {
        console.error("Failed to parse SSR financing plans data:", e);
        return [];
    }
    return check(FinancingPlans.decode(rawData));
};

/**
 * Parse and validate SSR concrete bundles data using io-ts codecs.
 */
export const parseSSRConcreteBundles = (
    concreteBundlesDataJson: string,
): readonly IConcreteBundle[] => {
    const trimmed = concreteBundlesDataJson?.trim();
    if (!trimmed || trimmed === "null" || trimmed === "[]") {
        return [];
    }
    let rawData: unknown;
    try {
        rawData = JSON.parse(trimmed);
    } catch (e) {
        console.error("Failed to parse SSR concrete bundles data:", e);
        return [];
    }
    return check(ConcreteBundles.decode(rawData));
};

/**
 * Parse and validate SSR upsell info modals data using io-ts codecs.
 */
export const parseSSRUpsellInfoModals = (
    upsellInfoModalsDataJson: string,
): readonly IModularConfiguratorUpsellInfoModal[] => {
    const trimmed = upsellInfoModalsDataJson?.trim();
    if (!trimmed || trimmed === "null" || trimmed === "[]") {
        return [];
    }
    let rawData: unknown;
    try {
        rawData = JSON.parse(trimmed);
    } catch (e) {
        console.error("Failed to parse SSR upsell info modals data:", e);
        return [];
    }
    return check(ModularConfiguratorUpsellInfoModals.decode(rawData));
};

/**
 * Parse and validate SSR promo cards data using io-ts codecs.
 */
export const parseSSRPromoCards = (
    promoCardsDataJson: string,
): readonly IModularConfiguratorPromoCard[] => {
    const trimmed = promoCardsDataJson?.trim();
    if (!trimmed || trimmed === "null" || trimmed === "[]") {
        return [];
    }
    let rawData: unknown;
    try {
        rawData = JSON.parse(trimmed);
    } catch (e) {
        console.error("Failed to parse SSR promo cards data:", e);
        return [];
    }
    return check(ModularConfiguratorPromoCards.decode(rawData));
};

/**
 * Parse and validate SSR reviews products data using io-ts codecs.
 */
export const parseSSRReviewsProducts = (
    productsDataJson: string,
): readonly IReviewsProduct[] => {
    const trimmed = productsDataJson?.trim();
    if (!trimmed || trimmed === "null" || trimmed === "[]") {
        return [];
    }
    let rawData: unknown;
    try {
        rawData = JSON.parse(trimmed);
    } catch (e) {
        console.error("Failed to parse SSR reviews products data:", e);
        return [];
    }
    return check(ReviewsProducts.decode(rawData));
};

/**
 * Parse and validate SSR review results page data using io-ts codecs.
 */
export const parseSSRReviewResultsPage = (
    reviewsDataJson: string,
): IReviewResultsPage | null => {
    const trimmed = reviewsDataJson?.trim();
    if (!trimmed || trimmed === "null") {
        return null;
    }
    let rawData: unknown;
    try {
        rawData = JSON.parse(trimmed);
    } catch (e) {
        console.error("Failed to parse SSR review results page data:", e);
        return null;
    }
    return check(ReviewResultsPage.decode(rawData));
};

/**
 * Parse and validate SSR search facets data using io-ts codecs.
 */
export const parseSSRSearchFacets = (
    facetsDataJson: string,
): readonly ISearchFacet[] => {
    const trimmed = facetsDataJson?.trim();
    if (!trimmed || trimmed === "null" || trimmed === "[]") {
        return [];
    }
    let rawData: unknown;
    try {
        rawData = JSON.parse(trimmed);
    } catch (e) {
        console.error("Failed to parse SSR search facets data:", e);
        return [];
    }
    return check(SearchFacets.decode(rawData));
};

/**
 * Parse and validate SSR write review template data using io-ts codecs.
 */
export const parseSSRWriteReviewTemplate = (
    templateDataJson: string,
): IWriteReviewTemplate | null => {
    const trimmed = templateDataJson?.trim();
    if (!trimmed || trimmed === "null" || trimmed === "{}") {
        return null;
    }
    let rawData: unknown;
    try {
        rawData = JSON.parse(trimmed);
    } catch (e) {
        console.error("Failed to parse SSR write review template data:", e);
        return null;
    }
    return check(WriteReviewTemplate.decode(rawData));
};

/**
 * A productID + template pair as serialized by the Python SSR context builder.
 */
interface ISSRReviewTemplatePair {
    readonly productID: IReviewsProductID;
    readonly template: IWriteReviewTemplate;
}

const SSRReviewTemplatePair = t.readonly(
    t.type({
        productID: ReviewsProductID,
        template: WriteReviewTemplate,
    }),
);

const SSRReviewTemplatePairs = t.readonlyArray(SSRReviewTemplatePair);

/**
 * Parse and validate SSR review template pairs (multi-product write review).
 */
export const parseSSRWriteReviewTemplates = (
    templatesDataJson: string,
): readonly ISSRReviewTemplatePair[] => {
    const trimmed = templatesDataJson?.trim();
    if (!trimmed || trimmed === "null" || trimmed === "[]") {
        return [];
    }
    let rawData: unknown;
    try {
        rawData = JSON.parse(trimmed);
    } catch (e) {
        console.error("Failed to parse SSR review templates data:", e);
        return [];
    }
    return check(SSRReviewTemplatePairs.decode(rawData));
};
